import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode } from "vue"

const _hoisted_1 = { class: "wrapper" }
const _hoisted_2 = { class: "main-table" }
const _hoisted_3 = {
  key: 0,
  class: "main-table__status"
}
const _hoisted_4 = { class: "main-table__message" }
const _hoisted_5 = { class: "main-table__footer" }

import { ref, onMounted, onBeforeUnmount } from "vue";
import PerfectScrollbar from "perfect-scrollbar";
import "perfect-scrollbar/css/perfect-scrollbar.css";
import Paginator from "@/components/modules/EventJournal/Paginator.vue";
import type {Paginator as PaginatorType} from "@/types/paginator";
import IconStatusAlert from "@/assets/icons/IconStatusAlert.vue";
import IconStatusInfo from "@/assets/icons/IconStatusInfo.vue";
import IconStatusError from "@/assets/icons/IconStatusError.vue";
import TableLoader from "@/components/ui/loaders/TableLoader.vue";
import TableEmptyData from "@/components/ui/loaders/TableEmptyData.vue";
import {EventLogItem} from "@/types/events";

interface MainTableProps {
  items: EventLogItem[];
  paginator: PaginatorType;
  loading: boolean;
}

interface MainTableEmits {
  (_e: "pageChange", _page: number): void;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'EventTable',
  props: {
    items: {},
    paginator: {},
    loading: { type: Boolean }
  },
  emits: ["pageChange"],
  setup(__props: any, { emit: __emit }) {

const props = __props;
const emit = __emit;

// Обработчик перехода на страницу
const handleGoToPage = (page: number) => {
  emit("pageChange", page);
  // Сбрасываем позицию скролла на начало при смене страницы
  if (scrollContainer.value) {
    scrollContainer.value.scrollTop = 0;
  }
};

// Конфигурация колонок: какие поля отображать и их заголовки
const columns = [
  { key: "timestamp", label: "Дата и время" },
  { key: "message", label: "Событие" },
  { key: "user_id", label: "Пользователь" }
];

// Реф на контейнер таблицы
const scrollContainer = ref<HTMLElement | null>(null);

// Флаг. Наличие скролла у контейнера таблицы
const hasScroll = ref(false);

// Объект скроллбара
let ps: PerfectScrollbar | null = null;
let resizeObserverInstance: ResizeObserver | null = null;

// Форматирование даты и времени
const formatDateTime = (timestamp: string) => {
  const utcTimestamp = timestamp.endsWith("Z") ? timestamp : timestamp + "Z";
  const date = new Date(utcTimestamp);

  return date.toLocaleString("ru-RU", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit"
  });
};

// Форматирование значения ячейки в зависимости от типа
const formatCellValue = (row: EventLogItem, columnKey: string) => {
  const value = row[columnKey as keyof EventLogItem];

  if (columnKey === "timestamp" && typeof value === "string") {
    return formatDateTime(value);
  }

  return value ?? "-";
};

// Проверка наличия скролла у контейнера таблицы.
const checkScroll = () => {
  if (scrollContainer.value) {
    hasScroll.value = scrollContainer.value.scrollHeight > scrollContainer.value.clientHeight;
  }
};

onMounted(() => {
  if (scrollContainer.value) {
    ps = new PerfectScrollbar(scrollContainer.value, {
      wheelSpeed: 1,
      wheelPropagation: false,
      minScrollbarLength: 20
    });

    checkScroll();

    // Наблюдаем за изменениями размера контейнера
    resizeObserverInstance = new ResizeObserver(() => {
      checkScroll();
      ps?.update();
    });
    resizeObserverInstance.observe(scrollContainer.value);
  }
});

onBeforeUnmount(() => {
  if (resizeObserverInstance) {
    resizeObserverInstance.disconnect();
    resizeObserverInstance = null;
  }
  if (ps) {
    ps.destroy();
    ps = null;
  }
});


return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", {
        class: _normalizeClass([{ 'main-table__header--with-scroll': hasScroll.value }, "main-table__header main-table__row main-table__row--header"])
      }, [
        (_openBlock(), _createElementBlock(_Fragment, null, _renderList(columns, (column) => {
          return _createElementVNode("div", {
            key: column.key,
            class: "main-table__col main-table__col--header"
          }, _toDisplayString(column.label), 1)
        }), 64))
      ], 2),
      _createElementVNode("div", {
        ref_key: "scrollContainer",
        ref: scrollContainer,
        class: "main-table__body"
      }, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(props.items, (row) => {
          return (_openBlock(), _createElementBlock("div", {
            key: row.id,
            class: _normalizeClass(["main-table__row", { 'main-table__row--with-scroll': hasScroll.value }])
          }, [
            (_openBlock(), _createElementBlock(_Fragment, null, _renderList(columns, (column) => {
              return _createElementVNode("div", {
                key: column.key,
                class: "main-table__col"
              }, [
                (column.key === 'message')
                  ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                      (!row.validation_status)
                        ? (_openBlock(), _createBlock(IconStatusAlert, { key: 0 }))
                        : _createCommentVNode("", true),
                      (row.validation_status === 'validated')
                        ? (_openBlock(), _createBlock(IconStatusInfo, { key: 1 }))
                        : _createCommentVNode("", true),
                      (row.validation_status === 'pre_validation_error')
                        ? (_openBlock(), _createBlock(IconStatusError, { key: 2 }))
                        : _createCommentVNode("", true)
                    ]))
                  : _createCommentVNode("", true),
                _createElementVNode("div", _hoisted_4, _toDisplayString(formatCellValue(row, column.key)), 1)
              ])
            }), 64))
          ], 2))
        }), 128)),
        _cache[0] || (_cache[0] = _createElementVNode("div", { class: "main-table__body--separator" }, null, -1)),
        (!__props.loading && props.items.length === 0)
          ? (_openBlock(), _createBlock(TableEmptyData, {
              key: 0,
              subtitle: "Данные по указанным параметрам не найдены"
            }))
          : _createCommentVNode("", true),
        (__props.loading)
          ? (_openBlock(), _createBlock(TableLoader, {
              key: 1,
              position: "absolute"
            }))
          : _createCommentVNode("", true)
      ], 512),
      _createElementVNode("div", _hoisted_5, [
        _createVNode(Paginator, {
          paginator: props.paginator,
          onGoToPage: handleGoToPage
        }, null, 8, ["paginator"])
      ])
    ])
  ]))
}
}

})